import type { ConditionOperator } from '../models/alert.types'
import type { AlertHistoryRow } from '../models/history.types'

import type { ChartRootProps } from '@/shared/ui/chart'
// Deep import, not the `@/shared/ui/chart` barrel: that barrel also re-exports `ChartRoot.vue`,
// which lazy-loads Highcharts — pulling it through here would defeat that lazy-load for every
// consumer of this (eagerly-bundled) service module.
import { buildStackedChartTooltip } from '@/shared/ui/chart/tooltip'

const DANGER_COLOR = 'var(--ui-color-danger)'

export type AlertTrendOperator =
  | ConditionOperator
  | 'greater_than'
  | 'greater_than_or_equal_to'
  | 'less_than'
  | 'less_than_or_equal_to'
  | 'increase'
  | 'decrease'

export interface AlertTrendOptionsContext {
  formatDate: (value: string) => string
  formatMetric: (value: number) => string
  labels: {
    title: string
    actual: string
    threshold: string
    alert: string
    alertTriggered: string
    metricLabel: string
  }
}

export type AlertTrendOptions = NonNullable<ChartRootProps['options']>

interface TooltipPoint {
  name?: string
  y?: number
  color?: string
}

interface TooltipContext {
  points?: TooltipPoint[]
}

interface AxisLabelContext {
  value: string | number
}

function operatorDirection(operator: AlertTrendOperator): 'up' | 'down' | null {
  if (
    operator === '>' ||
    operator === '>=' ||
    operator === 'greater_than' ||
    operator === 'greater_than_or_equal_to' ||
    operator === 'increase'
  ) {
    return 'up'
  }
  if (
    operator === '<' ||
    operator === '<=' ||
    operator === 'less_than' ||
    operator === 'less_than_or_equal_to' ||
    operator === 'decrease'
  ) {
    return 'down'
  }
  return null
}

/** Resolve the chart threshold into the metric's unit while retaining the baseline separately. */
export function resolveAlertThreshold(
  baseline: number | null,
  amount: number,
  isPercentage: boolean,
  operator: AlertTrendOperator,
): number | null {
  if (!Number.isFinite(amount)) return null
  if (!isPercentage) return amount
  if (baseline === null || !Number.isFinite(baseline)) return null
  const direction = operatorDirection(operator)
  if (!direction) return null
  const ratio = amount / 100
  return baseline * (direction === 'up' ? 1 + ratio : 1 - ratio)
}

export function isAlertThresholdBreached(
  value: number,
  threshold: number,
  operator: AlertTrendOperator,
): boolean {
  if (operator === '>' || operator === 'greater_than' || operator === 'increase') {
    return value > threshold
  }
  if (operator === '>=' || operator === 'greater_than_or_equal_to') {
    return value >= threshold
  }
  if (operator === '<' || operator === 'less_than' || operator === 'decrease') {
    return value < threshold
  }
  if (operator === '<=' || operator === 'less_than_or_equal_to') {
    return value <= threshold
  }
  return false
}

function rowDate(row: Record<string, number | string>): string | null {
  return typeof row.date === 'string' ? row.date : null
}

function rowValue(row: Record<string, number | string>, field: string): number {
  const value = row[field]
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value !== 'string' || !value.trim()) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function alertTrendIndex(
  history: AlertHistoryRow['history'],
  compareDate: string | null,
): number {
  if (!compareDate || !history.length) return -1
  const exact = history.findIndex((row) => rowDate(row) === compareDate)
  if (exact !== -1) return exact
  return history.findIndex((row) => {
    const date = rowDate(row)
    return date !== null && date >= compareDate
  })
}

export function buildAlertTrendOptions(
  row: AlertHistoryRow,
  context: AlertTrendOptionsContext,
): AlertTrendOptions {
  const alertIndex = alertTrendIndex(row.history, row.compareDate)
  // Legacy parity: old's own reference line plots the raw baseline, not a percentage-resolved
  // threshold (that computation exists in old's source but is dead/commented out) — breach
  // coloring uses the same value the line is drawn at, for the two to visually agree.
  const points = row.history.map((historyRow) => {
    const date = rowDate(historyRow)
    const value = rowValue(historyRow, row.metricField)
    const breached =
      row.baseline !== null && isAlertThresholdBreached(value, row.baseline, row.operator)
    const afterAlert = Boolean(row.compareDate && date && date >= row.compareDate)
    const color = breached && afterAlert ? DANGER_COLOR : 'var(--ui-color-chart-accent)'
    return {
      name: date ? context.formatDate(date) : '',
      y: value,
      color,
      marker: { fillColor: color, lineColor: color, radius: 4 },
    }
  })

  return {
    exporting: { enabled: false },
    chart: { type: 'line', height: 300, backgroundColor: 'transparent' },
    title: {
      text: context.labels.title,
      align: 'left',
      style: { color: 'var(--ui-color-fg)', fontSize: '14px', fontWeight: '600' },
    },
    credits: { enabled: false },
    xAxis: {
      categories: points.map((point) => point.name),
      labels: { style: { color: 'var(--ui-color-subtle)', fontSize: '11px' } },
      lineColor: 'var(--ui-color-border)',
      tickmarkPlacement: 'on',
      plotLines:
        alertIndex === -1
          ? []
          : [
              {
                value: alertIndex,
                color: DANGER_COLOR,
                dashStyle: 'Dash',
                width: 2,
                // Highcharts inserts chart elements into the SVG in ascending `zIndex` order, and
                // the tooltip's own (internal, non-configurable) zIndex sits between this and the
                // y-axis plot line's `5` below — `10` was high enough to land AFTER the tooltip in
                // DOM order, painting over it on hover. `5` matches the y-axis line, which already
                // sorted correctly.
                zIndex: 5,
                label: {
                  text: context.labels.alert,
                  align: 'center',
                  rotation: 0,
                  y: -5,
                  style: { color: DANGER_COLOR, fontSize: '10px', fontWeight: '600' },
                },
              },
            ],
    },
    yAxis: {
      title: { text: '' },
      gridLineDashStyle: 'Dash',
      gridLineColor: 'var(--ui-color-border)',
      labels: {
        formatter(this: AxisLabelContext) {
          return context.formatMetric(Number(this.value))
        },
        style: { color: 'var(--ui-color-subtle)', fontSize: '11px' },
      },
      plotLines:
        row.baseline === null
          ? []
          : [
              {
                value: row.baseline,
                color: DANGER_COLOR,
                dashStyle: 'Dash' as const,
                width: 2,
                zIndex: 5,
                label: {
                  text: `${context.labels.threshold}: ${context.formatMetric(row.baseline)}`,
                  align: 'right' as const,
                  x: -10,
                  y: -10,
                  style: { color: DANGER_COLOR, fontSize: '10px', fontWeight: '500' },
                },
              },
            ],
    },
    tooltip: {
      shared: true,
      backgroundColor: 'var(--ui-color-surface)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      // Legacy parity (`AlertSideChart.vue`'s tooltip): uppercase date, then the metric's own
      // display name (not a generic "Actual" label), then the value colored to match the point.
      // The markup itself is built by the shared, escaped `buildStackedChartTooltip` helper
      // (outside this feature) — this feature's own ledger (ORP-18769) bans building markup
      // strings directly in its scripts/services.
      formatter(this: TooltipContext) {
        const point = this.points?.[0]
        if (!point || typeof point.y !== 'number') return ''
        return buildStackedChartTooltip(
          (point.name ?? '').toUpperCase(),
          context.labels.metricLabel,
          context.formatMetric(point.y),
          point.color,
        )
      },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { color: 'var(--ui-color-muted)', fontSize: '12px', fontWeight: '400' },
    },
    plotOptions: {
      series: {
        lineWidth: 2.5,
        marker: { enabled: true, symbol: 'circle' },
        states: { hover: { lineWidthPlus: 0 } },
      },
    },
    series: [
      {
        type: 'line',
        name: context.labels.actual,
        data: points,
        color: 'var(--ui-color-chart-accent)',
        zIndex: 2,
      },
      // Legend-only proxies: a `yAxis.plotLine` never gets its own legend entry in Highcharts —
      // old shows one anyway, so these two empty series exist purely to draw it.
      ...(row.baseline === null
        ? []
        : [
            {
              type: 'line' as const,
              name: `${context.labels.threshold} (${context.formatMetric(row.baseline)})`,
              data: [],
              color: DANGER_COLOR,
              dashStyle: 'Dash' as const,
              enableMouseTracking: false,
              showInLegend: true,
            },
          ]),
      {
        type: 'scatter',
        name: context.labels.alertTriggered,
        data: [],
        color: DANGER_COLOR,
        marker: { symbol: 'circle' },
        enableMouseTracking: false,
        showInLegend: true,
      },
    ],
  } as AlertTrendOptions
}
